import CodeIcon from "@mui/icons-material/Code";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SortIcon from "@mui/icons-material/Sort";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Box,
	Chip,
	IconButton,
	Pagination,
	Paper,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import type React from "react";
import { useCallback } from "react";
import { useAuth } from "@/features/auth";
import { useDialogState, usePagination, useSortOrder } from "@/shared/hooks";
import type { Information } from "@/shared/lib/api/api-client";
import { formatDate } from "@/shared/utils";
import { useInformationAppDisplay, useInformationDelete } from "../hooks";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import EditInformationDialog from "./EditInformationDialog";
import InformationDetailDialog from "./InformationDetailDialog";
import InformationSourceDialog from "./InformationSourceDialog";

interface InformationsListProps {
	jsonContents: Information[];
	onRefresh: () => void | Promise<void>;
	onShowSnackbar: (message: string, severity: "success" | "error", customMessage?: string) => void;
}

type SortOrder = "newest" | "oldest";

export default function InformationsList({
	jsonContents,
	onRefresh,
	onShowSnackbar,
}: InformationsListProps): React.ReactElement {
	const { usergroup } = useAuth();
	const isUserAdmin = usergroup === "管理者";

	// ソート機能
	const sortFn = useCallback((items: Information[], order: SortOrder) => {
		return [...items].sort((a, b) => {
			const dateA = new Date(a.date).getTime();
			const dateB = new Date(b.date).getTime();
			return order === "newest" ? dateB - dateA : dateA - dateB;
		});
	}, []);

	const { sortOrder, sortedItems, handleSortChange } = useSortOrder({
		items: jsonContents,
		initialOrder: "newest" as SortOrder,
		sortFn,
	});

	// ページネーション機能
	const { page, totalPages, currentItems, handlePageChange, setPage } = usePagination({
		items: sortedItems,
		itemsPerPage: 10,
	});

	// アプリ表示判定
	const { isInAppDisplay } = useInformationAppDisplay({
		items: jsonContents,
	});

	// ダイアログ状態管理
	const deleteDialog = useDialogState<Information>();
	const detailDialog = useDialogState<Information>();
	const sourceDialog = useDialogState<Information>();
	const editDialog = useDialogState<Information>();

	// 削除処理
	const { deleteInformation } = useInformationDelete({
		onSuccess: (message) => {
			onRefresh();
			onShowSnackbar(message || "削除しました", "success");
		},
		onError: (message) => {
			onShowSnackbar(message, "error");
		},
	});

	// イベントハンドラー
	const handleSortOrderChange = useCallback(
		(_event: React.MouseEvent<HTMLElement>, newOrder: SortOrder | null) => {
			handleSortChange(_event, newOrder);
			setPage(1); // ソート変更時は1ページ目に戻る
		},
		[handleSortChange, setPage],
	);

	const handleDeleteConfirm = useCallback(async () => {
		if (!deleteDialog.dialogState.data) return;

		deleteDialog.closeDialog();

		const { shouldDecrementPage } = await deleteInformation(
			deleteDialog.dialogState.data,
			currentItems.length,
			page,
		);

		if (shouldDecrementPage) {
			setPage(page - 1);
		}
	}, [deleteDialog, deleteInformation, currentItems.length, page, setPage]);

	const handleEditSuccess = useCallback(
		(message?: string) => {
			editDialog.closeDialog();
			onRefresh();
			onShowSnackbar(message || "更新しました", "success");
		},
		[editDialog, onRefresh, onShowSnackbar],
	);

	const handleEditError = useCallback(
		(message: string) => {
			onShowSnackbar(message, "error");
		},
		[onShowSnackbar],
	);

	return (
		<Box
			sx={{
				width: "100%",
				maxWidth: 1200,
				display: "flex",
				flexDirection: "column",
			}}
		>
			{/* ヘッダー */}
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					<Typography sx={{ fontWeight: "bold", fontSize: "18px" }}>
						配信済みのお知らせ一覧
					</Typography>
					<Chip
						label={`全 ${sortedItems.length} 件`}
						size="small"
						color="primary"
						variant="outlined"
					/>
				</Box>

				{/* ソート切り替え */}
				<ToggleButtonGroup
					value={sortOrder}
					exclusive
					onChange={handleSortOrderChange}
					size="small"
					aria-label="sort order"
				>
					<ToggleButton value="newest" aria-label="newest first">
						<SortIcon sx={{ mr: 0.5 }} fontSize="small" />
						最新順
					</ToggleButton>
					<ToggleButton value="oldest" aria-label="oldest first">
						<SortIcon sx={{ mr: 0.5, transform: "scaleY(-1)" }} fontSize="small" />
						古い順
					</ToggleButton>
				</ToggleButtonGroup>
			</Box>

			{jsonContents.length === 0 ? (
				<Paper
					sx={{
						p: 4,
						textAlign: "center",
						backgroundColor: "#f5f5f5",
					}}
				>
					<Typography variant="body1" color="textSecondary">
						お知らせがありません
					</Typography>
				</Paper>
			) : (
				<>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						{currentItems.map((info) => {
							return (
								<Paper
									key={info.id}
									sx={{
										p: 3,
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										transition: "box-shadow 0.3s",
										border: "1px solid #e0e0e0",
										backgroundColor: "#fff",
										"&:hover": {
											boxShadow: 4,
										},
									}}
								>
									<Box sx={{ flex: 1 }}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
											<Typography
												sx={{
													color: "#666",
													fontSize: "12px",
												}}
											>
												{formatDate(info.date)}
											</Typography>
											{(() => {
												const now = new Date();
												const infoDate = new Date(info.date);

												// 未来の日時の場合は予約配信
												if (infoDate > now) {
													return (
														<Chip
															label={"予約配信"}
															size="small"
															color="warning"
															sx={{ height: 20, fontSize: "11px" }}
														/>
													);
												}

												// アプリに表示中（最新10件）の場合は配信中
												if (isInAppDisplay(info.id)) {
													return (
														<Chip
															label={"配信中"}
															size="small"
															color="success"
															sx={{ height: 20, fontSize: "11px" }}
														/>
													);
												}

												return null;
											})()}
										</Box>
										<Typography
											sx={{
												fontSize: "16px",
												fontWeight: "bold",
												mb: 1,
											}}
										>
											{info.title}
										</Typography>
										<Typography
											sx={{
												fontSize: "14px",
												color: "#555",
												overflow: "hidden",
												textOverflow: "ellipsis",
												display: "-webkit-box",
												WebkitLineClamp: 2,
												WebkitBoxOrient: "vertical",
												wordBreak: "break-word",
											}}
										>
											{info.body.replace(/<[^>]*>/g, "")}
										</Typography>
									</Box>
									<Box sx={{ display: "flex", gap: 1, ml: 2 }}>
										<IconButton
											onClick={() => detailDialog.openDialog(info)}
											color="info"
											aria-label="view"
										>
											<Tooltip title="プレビュー">
												<VisibilityIcon />
											</Tooltip>
										</IconButton>
										<IconButton
											onClick={() => sourceDialog.openDialog(info)}
											sx={{ color: "text.secondary" }}
											aria-label="source"
										>
											<Tooltip title="ソースを表示">
												<CodeIcon />
											</Tooltip>
										</IconButton>
										{isUserAdmin && (
											<>
												<IconButton
													onClick={() => editDialog.openDialog(info)}
													color="primary"
													aria-label="edit"
												>
													<Tooltip title="編集">
														<EditIcon />
													</Tooltip>
												</IconButton>
												<IconButton
													onClick={() => deleteDialog.openDialog(info)}
													color="error"
													aria-label="delete"
												>
													<Tooltip title="削除">
														<DeleteIcon />
													</Tooltip>
												</IconButton>
											</>
										)}
									</Box>
								</Paper>
							);
						})}
					</Box>

					{/* ページネーション */}
					{totalPages > 1 && (
						<Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
							<Pagination
								count={totalPages}
								page={page}
								onChange={handlePageChange}
								color="primary"
								size="large"
								showFirstButton
								showLastButton
							/>
						</Box>
					)}
				</>
			)}

			<DeleteConfirmDialog
				open={deleteDialog.dialogState.open}
				title={deleteDialog.dialogState.data?.title || ""}
				onConfirm={handleDeleteConfirm}
				onCancel={deleteDialog.closeDialog}
			/>

			<InformationDetailDialog
				open={detailDialog.dialogState.open}
				info={detailDialog.dialogState.data}
				onClose={detailDialog.closeDialog}
			/>

			<InformationSourceDialog
				open={sourceDialog.dialogState.open}
				info={sourceDialog.dialogState.data}
				onClose={sourceDialog.closeDialog}
			/>

			<EditInformationDialog
				open={editDialog.dialogState.open}
				info={editDialog.dialogState.data}
				onClose={editDialog.closeDialog}
				onSuccess={handleEditSuccess}
				onError={handleEditError}
			/>
		</Box>
	);
}
